/**
 * ThemeSwitcher — 3 professionally crafted themes:
 * Light (clean X/LinkedIn inspired) | Onyx (true black, kept) | Colorful (vibrant)
 * Uses next-themes with data-theme attribute.
 */
import { useState, useEffect } from "react";
import { Sun, Moon, Palette } from "lucide-react";

const THEMES = [
  { key: "light",     label: "Light",     icon: Sun,    desc: "Clean white, high contrast" },
  { key: "onyx",      label: "Onyx",      icon: Moon,   desc: "True black, neon glow" },
  { key: "colorful",  label: "Colorful",  icon: Palette,desc: "Vibrant purple-gold" },
];

function applyTheme(modeKey) {
  // Set data-theme for CSS variable overrides
  document.documentElement.setAttribute("data-theme", modeKey);
  // Toggle .dark class so Tailwind dark: variants work
  if (modeKey === "light") {
    document.documentElement.classList.remove("dark");
  } else {
    document.documentElement.classList.add("dark");
  }
  // Persist
  try { localStorage.setItem("allbaise-theme", modeKey); } catch {}
}

function getStoredTheme() {
  try { return localStorage.getItem("allbaise-theme") || "onyx"; } catch { return "onyx"; }
}

export default function ThemeSwitcher({ compact = false }) {
  const [mounted, setMounted] = useState(false);
  const [activeTheme, setActiveTheme] = useState("onyx");

  useEffect(() => {
    const stored = getStoredTheme();
    setActiveTheme(stored);
    applyTheme(stored);
    setMounted(true);
  }, []);

  if (!mounted) return null;

  const handleTheme = (key) => {
    setActiveTheme(key);
    applyTheme(key);
  };

  if (compact) {
    return (
      <div className="flex items-center gap-1 bg-white/5 rounded-lg p-0.5">
        {THEMES.map(t => {
          const Icon = t.icon;
          const isActive = activeTheme === t.key;
          return (
            <button
              key={t.key}
              onClick={() => handleTheme(t.key)}
              className={`p-1.5 rounded-md transition-all ${
                isActive ? "bg-violet-600 text-white shadow-sm" : "text-zinc-500 hover:text-zinc-300"
              }`}
              title={`${t.label} — ${t.desc}`}
            >
              <Icon className="w-4 h-4" />
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-zinc-500">Theme</span>
      <div className="flex items-center gap-1 bg-white/5 rounded-lg p-0.5">
        {THEMES.map(t => {
          const Icon = t.icon;
          const isActive = activeTheme === t.key;
          return (
            <button
              key={t.key}
              onClick={() => handleTheme(t.key)}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-all ${
                isActive ? "bg-violet-600 text-white shadow-sm" : "text-zinc-400 hover:text-zinc-200"
              }`}
              title={t.desc}
            >
              <Icon className="w-3.5 h-3.5" />
              {t.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
